import { FoodLibraryMeal } from "@/clients/models/FoodLibraryMeal";
import { MealTime } from "@/clients/models/MealTime";
import { ChevronDownIcon } from "@/components/icons/chevron-down-icon";
import IconButton from "@/components/ui/buttons/IconButton";
import { TextButton } from "@/components/ui/buttons/TextButton";
import { DislikeMealButton } from "@/features/meal-plan/components/dislike-meal-button";
import { EditMealButton } from "@/features/meal-plan/components/edit-meal-button";
import { IngredientsModal } from "@/features/meal-plan/components/ingredients-modal";
import { LikeMealButton } from "@/features/meal-plan/components/like-meal-button";
import { MealAllergensTags } from "@/features/meal-plan/components/meal-allergens-tags";
import { MealDescription } from "@/features/meal-plan/components/meal-description";
import { MealDetailMacros } from "@/features/meal-plan/components/meal-detail-macros";
import {
  AmendMacrosModal,
  NutritionValues,
} from "@/features/meal-plan/components/meal-detail-macros/amend-macros-modal";
import { MealDetailMeta } from "@/features/meal-plan/components/meal-detail-meta";
import { MealDetailTags } from "@/features/meal-plan/components/meal-detail-tags";
import { MealFeedbackSection } from "@/features/meal-plan/components/meal-feedback-section";
import { MealIngredientsSection } from "@/features/meal-plan/components/meal-ingredients-section";
import { MethodModal } from "@/features/meal-plan/components/method-modal";
import { PlanOverviewImage } from "@/features/meal-plan/components/plan-overview-image";
import { useEditMealNutrition } from "@/hooks/requests/meal/use-edit-meal-nutrition";
import { useGetOtherPreferences } from "@/hooks/requests/other-preferences/use-get-other-preferences";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { createAnimation, IonContent, IonModal } from "@ionic/react";
import { memo, useCallback, useRef, useState } from "react";

const slideUpEnter = (baseEl: HTMLElement) => {
  const wrapper = baseEl.shadowRoot?.querySelector(".modal-wrapper") as HTMLElement | null;
  return createAnimation()
    .addElement(baseEl)
    .duration(320)
    .easing("cubic-bezier(0.32, 0.72, 0, 1)")
    .addAnimation(
      createAnimation()
        .addElement(wrapper ?? baseEl)
        .fromTo("transform", "translateY(100%)", "translateY(0)"),
    );
};

const slideDownLeave = (baseEl: HTMLElement) => slideUpEnter(baseEl).direction("reverse");

interface MealDetailModalProps {
  meal: FoodLibraryMeal | null;
  isOpen: boolean;
  onDismiss: () => void;
  onMealUpdated: (meal: FoodLibraryMeal) => void;
}

export const MealDetailModal = memo(function MealDetailModal({
  meal,
  isOpen,
  onDismiss,
  onMealUpdated,
}: MealDetailModalProps) {
  const { otherPreferences } = useGetOtherPreferences();
  const showCaloriesAndMacros = otherPreferences?.showCaloriesAndMacros ?? false;
  const pageRef = useRef<HTMLElement>(null);
  const ingredientsSectionRef = useRef<HTMLDivElement>(null);
  const [isIngredientsModalOpen, setIsIngredientsModalOpen] = useState(false);
  const [isMethodModalOpen, setIsMethodModalOpen] = useState(false);
  const [isNutritionEditOpen, setIsNutritionEditOpen] = useState(false);
  const { editMealNutritionAsync, isEditingMealNutrition } = useEditMealNutrition();
  const { showError, showSuccess } = useToast();

  const scrollToIngredients = useCallback(() => {
    ingredientsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleNutritionSave = useCallback(
    async ({ calories, proteinG, carbsG, fatsG }: NutritionValues) => {
      if (!meal?.id) {
        showError("This meal is missing an ID and cannot be updated.");
        return;
      }

      try {
        const result = await editMealNutritionAsync({
          mealId: meal.id,
          requestBody: {
            nutrition: {
              energy_kcal: calories,
              protein_grams: proteinG,
              carbs_grams: carbsG,
              fat_grams: fatsG,
            },
          },
          updateMealDetail: false,
        });

        onMealUpdated({
          ...meal,
          ...result.meal,
          isFavourite: meal.isFavourite,
          useCount: meal.useCount,
          lastUsedAt: meal.lastUsedAt,
          favouriteMealTimes: meal.favouriteMealTimes,
        });
        showSuccess(result.was_forked ? "Meal saved to Your Foods." : "Nutrition updated.");
        setIsNutritionEditOpen(false);
      } catch (error) {
        showError(error instanceof Error ? error.message : "Unable to update nutrition.");
      }
    },
    [editMealNutritionAsync, meal, onMealUpdated, showError, showSuccess],
  );

  if (!meal) return null;

  const mealTime = meal.favouriteMealTimes?.[0] ?? MealTime.DINNER;
  const notLikeable = meal.isYourFood ?? false;
  const editable = Boolean(meal.id);
  const hasIngredients = meal.ingredients?.length > 0;
  const hasInstructions = (meal.instructions?.length ?? 0) > 0;
  const hasActions = hasIngredients || hasInstructions;
  const hasImage = Boolean(meal.imageUri);

  const renderControls = (onImage: boolean) => (
    <div
      className={cn(
        "safe-area-top flex items-center justify-between px-3",
        onImage ? "absolute inset-x-0 top-3 z-10" : "py-2",
      )}
    >
      <IconButton
        aria-label="Close"
        onClick={onDismiss}
        className="bg-white/70 backdrop-blur-sm hover:bg-white/80 active:bg-white/80"
      >
        <ChevronDownIcon />
      </IconButton>

      {editable || !notLikeable ? (
        <div className="flex h-11 items-center rounded-full bg-white/70 px-1.5 backdrop-blur-sm">
          {editable && (
            <EditMealButton
              className="h-11 w-11 rounded-full"
              ariaLabel="Edit meal nutrition"
              onEdit={() => setIsNutritionEditOpen(true)}
            />
          )}
          {!notLikeable && (
            <>
              <LikeMealButton
                meal={meal}
                mealTime={mealTime}
                className="flex-1 p-0"
                filledColor="red"
              />
              <DislikeMealButton meal={meal} className="flex-1 p-0" />
            </>
          )}
        </div>
      ) : null}
    </div>
  );

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      enterAnimation={slideUpEnter}
      leaveAnimation={slideDownLeave}
    >
      <IonContent scrollY={false} forceOverscroll={false}>
        <div ref={pageRef as React.RefObject<HTMLDivElement>} className="flex h-full flex-col">
          <div className="no-scrollbar flex-1 overflow-y-auto">
            <div className="relative shrink-0">
              <PlanOverviewImage
                imageUri={meal.imageUri ?? ""}
                name={meal.name}
                notLikeable={notLikeable}
                imageHasWhiteBackground={meal.imageHasWhiteBackground ?? false}
              />
              {hasImage && (
                <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/30 to-transparent" />
              )}
              {renderControls(true)}
            </div>

            <div className={cn("flex flex-col gap-6 px-4 pt-6", hasActions ? "pb-6" : "pb-10")}>
              <h1 className={cn(textVariants.mealTitle, "mx-4 text-center")}>{meal.name}</h1>

              <MealDetailTags categoryPills={meal.categoryPills ?? []} />

              <MealDetailMeta prepTime={meal.totalTime ?? null} portionSize={meal.portionSize} />

              <MealDescription description={meal.description} />

              <MealAllergensTags allergens={meal.allergens} />

              {showCaloriesAndMacros && <MealDetailMacros nutritionalInfo={meal.nutritionalInfo} />}

              <div ref={ingredientsSectionRef}>
                <MealIngredientsSection meal={meal} fromPlan={false} />
              </div>

              <MealFeedbackSection mealId={meal.id} />
            </div>

            {hasActions && (
              <div className="flex justify-center pb-10">
                <div className="flex items-center gap-1 rounded-[50px] bg-[rgba(24,29,39,0.9)] px-6 py-1 whitespace-nowrap">
                  {hasIngredients && (
                    <TextButton
                      color="primary"
                      onClick={scrollToIngredients}
                      className="h-11 w-fit px-2 py-3 text-white transition-all hover:opacity-80"
                      aria-label="View ingredients"
                    >
                      Ingredients
                    </TextButton>
                  )}
                  {hasInstructions && (
                    <TextButton
                      color="primary"
                      onClick={() => setIsMethodModalOpen(true)}
                      className="h-11 w-fit px-2 py-3 text-white transition-all hover:opacity-80"
                      aria-label="View cooking method"
                    >
                      Method
                    </TextButton>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <IngredientsModal
          pageRef={pageRef}
          isOpen={isIngredientsModalOpen}
          onDismiss={() => setIsIngredientsModalOpen(false)}
          meal={meal}
          fromPlan={false}
        />

        <MethodModal
          pageRef={pageRef}
          isOpen={isMethodModalOpen}
          onDismiss={() => setIsMethodModalOpen(false)}
          meal={meal}
        />

        {editable && (
          <AmendMacrosModal
            isOpen={isNutritionEditOpen}
            onDismiss={() => setIsNutritionEditOpen(false)}
            onSave={handleNutritionSave}
            isSaving={isEditingMealNutrition}
            mealName={meal.name ?? ""}
            calories={meal.nutritionalInfo.perPortion?.energy?.kcal ?? 0}
            proteinG={meal.nutritionalInfo.perPortion?.protein?.grams ?? 0}
            carbsG={meal.nutritionalInfo.perPortion?.carbohydrate?.total?.grams ?? 0}
            fatsG={meal.nutritionalInfo.perPortion?.fat?.total?.grams ?? 0}
          />
        )}
      </IonContent>
    </IonModal>
  );
});
